import { ft } from '../core/units.js';
import { segmentClear } from '../core/collision.js';
import { OBJECTS } from '../data/objects.js';
import { impactEnergy, throwArc } from './mass.js';
import { EVENT } from './events.js';
import { NO_PERKS } from './perks.js';

/**
 * Things in the air, and what happens when they stop being in the air.
 *
 * One path. A wine bottle, an orange, a banquet chair and a caught scorpion are
 * all the same object here: a mass with a velocity. What is different between
 * them is entirely in the catalogue — how heavy, how loud, whether it survives
 * the landing — and nothing in this file branches on a kind.
 *
 * On impact it emits and never decides. It says a thing hit a thing at an
 * energy and a noise happened at a radius. Whether anybody heard it, whether it
 * is a crime, and whose fault it was are all somebody else's problem, and that
 * separation is the whole reason a thrown orange and a dropped tray can produce
 * the same consequences without either of them being written down twice.
 */

/** Metres per second squared. The same figure the character falls at. */
const GRAVITY = 24;

/** How wide a body is, for deciding whether a flying thing hit one. */
const BODY_RADIUS = 0.42;
const BODY_TOP = 1.75;

/** Below this the thing has stopped and becomes furniture again. */
const REST_SPEED = 1.2;

/** Seconds a projectile may live before it is simply put on the floor. */
const MAX_FLIGHT = 6;

/**
 * How far off a body a throw may be and still find it, per radian of perk.
 *
 * The cone is horizontal only. A throw's loft comes from the mass and is the
 * part of the arc a player is *supposed* to learn, so the class teaches leading
 * a target rather than teaching the game to lob for you: at the top tier a
 * bottle aimed ten degrees off somebody walking still catches them, and one
 * lobbed short still lands short.
 */
export function createThrowing({ world, entities, bus, bodies, perk = () => NO_PERKS.throwing }) {
  /** In flight. Not in the entity store's world state; owned here. */
  let flying = [];

  /**
   * Launches something.
   *
   * `power` is the fraction of a full wind-up. The arc comes from the mass and
   * nothing else, which is why a fire extinguisher is a lob and a napkin is a
   * dart without either being described anywhere.
   */
  /**
   * The nearest body inside the aim cone, or nothing.
   *
   * Nearest *by angle* rather than by distance, because the thing the player
   * was nearly pointing at is the thing they meant, and a closer body further
   * off the line is one they had already decided not to hit. Behind you never
   * counts, whatever the cone says.
   */
  function nudged(from, yaw, cone) {
    if (cone <= 0) return yaw;
    let best = null;
    for (const body of bodies()) {
      const dx = body.position.x - from.x;
      const dz = body.position.z - from.z;
      const range = Math.hypot(dx, dz);
      if (range < 0.5) continue;
      const to = Math.atan2(-dx, -dz);
      let off = to - yaw;
      while (off > Math.PI) off -= Math.PI * 2;
      while (off < -Math.PI) off += Math.PI * 2;
      if (Math.abs(off) > cone) continue;
      if (!best || Math.abs(off) < Math.abs(best.off)) best = { off, to };
    }
    return best ? best.to : yaw;
  }

  function launch({ kind, variant = null, from, yaw, pitch = 0, power = 1, actor = 'player', claim = null }) {
    const arc = throwArc(kind, power);
    /*
     * The two class perks, and they are the only thing in this file that knows
     * a class exists. Both are identities at tier zero: `range` is 1 and `aim`
     * is 0, so an untaught throw is byte for byte the throw this file has
     * always made. Only the player's own throws are taught ones.
     */
    const taught = actor === 'player' ? perk() : NO_PERKS.throwing;
    const aimed = nudged(from, yaw, taught.aim);
    const speed = arc.speed * taught.range;
    const angle = pitch + arc.loft;
    const horizontal = Math.cos(angle) * speed;
    const shot = {
      id: `f${flying.length}-${Math.random().toString(36).slice(2, 7)}`,
      kind,
      variant,
      claim,
      actor,
      position: { x: from.x, y: from.y, z: from.z },
      velocity: {
        x: -Math.sin(aimed) * horizontal,
        y: Math.sin(angle) * speed,
        z: -Math.cos(aimed) * horizontal,
      },
      age: 0,
    };
    flying = [...flying, shot];
    /*
     * `position` as well as `from`, and they are the same point.
     *
     * Every other event on the bus says `position`, and it is the one field
     * perception reads to decide where a thing happened. A throw said `from`
     * and nothing else — so for as long as this file has existed, every throw
     * in the game has been witnessed *at the world origin*: whether anybody saw
     * you throw a bottle was a question about whether they could see a spot in
     * the service corridor, and the record kept the origin as the place it
     * happened.
     *
     * It went unnoticed because it fails quietly in both directions and the
     * aftermath path papered over the rest — a witness who could not see the
     * origin still got a claim about it six seconds later, floored at a third.
     * Tightening the aftermath took the paper away and the throw check dropped
     * to nothing, which is how this surfaced. `from` is kept because the arc
     * reads it.
     */
    bus.emit(EVENT.THROW, {
      actor, kind, variant, from: { ...from }, position: { ...from }, power,
    });
    return shot;
  }

  /**
   * Puts something down.
   *
   * Not a weak throw — a separate verb on a separate event, and deliberately
   * never breaks anything. Dropping the thing you were just seen holding is the
   * counterplay to being suspected of throwing it, and a counterplay that
   * shatters a wine bottle at your feet is not a counterplay.
   *
   * It went out on the noise bus for a long time, labelled `drop`, which was
   * *nearly* right and was one careless commit away from being wrong: a label
   * on a shared event is a convention, and the only thing keeping a drop from
   * being weighed like the thing it was cleaning up after was a table that
   * happened not to mention it. `EVENT.DROP` makes it a fact about the world
   * instead, with its own row in the weights and its own rule about what it may
   * never do, both of them in `suspicion.js` where every other such rule is.
   *
   * It still carries a radius, because somebody standing next to you does hear
   * you set a bottle on the floor. Three feet of one.
   */
  function drop({ kind, variant = null, claim = null, at, actor = 'player' }) {
    const entity = entities.spawn({
      kind,
      variant,
      claim,
      position: { x: at.x, y: 0, z: at.z },
    });
    bus.emit(EVENT.DROP, {
      actor,
      kind,
      variant,
      position: { x: at.x, y: 0, z: at.z },
      radius: ft(3),
    });
    return entity;
  }

  /** Whether a segment passes through a standing body. */
  function bodyHit(from, to) {
    for (const body of bodies()) {
      const dx = to.x - from.x;
      const dz = to.z - from.z;
      const length = Math.hypot(dx, dz);
      if (length < 1e-6) continue;
      // Closest approach of the segment to the body's axis, in plan.
      const t = Math.max(
        0,
        Math.min(1, ((body.position.x - from.x) * dx + (body.position.z - from.z) * dz) / (length * length))
      );
      const px = from.x + dx * t;
      const pz = from.z + dz * t;
      if (Math.hypot(body.position.x - px, body.position.z - pz) > BODY_RADIUS) continue;
      const y = from.y + (to.y - from.y) * t;
      if (y < body.position.y || y > body.position.y + BODY_TOP) continue;
      return { body, at: { x: px, y, z: pz } };
    }
    return null;
  }

  /**
   * Everything an impact means.
   *
   * Emitted, never acted on. The energy decides whether a fragile thing
   * survives; the catalogue decides how loud it is either way; debris is
   * spawned as ordinary world entities, which is what makes broken glass
   * something you can pick up and throw again with no code for it.
   */
  function resolve(shot, at, target) {
    const definition = OBJECTS[shot.kind];
    const speed = Math.hypot(shot.velocity.x, shot.velocity.y, shot.velocity.z);
    const energy = impactEnergy(shot.kind, speed);
    const fragile = definition.fragile;
    const broke = Boolean(fragile) && energy >= fragile.threshold;

    bus.emit(EVENT.IMPACT, {
      actor: shot.actor,
      kind: shot.kind,
      variant: shot.variant,
      claim: shot.claim,
      target,
      position: { ...at },
      energy,
      speed,
      broke,
    });

    bus.emit(EVENT.NOISE, {
      actor: shot.actor,
      cause: broke ? 'break' : 'impact',
      kind: shot.kind,
      position: { ...at },
      // A break is heard much further than a bump, and a heavier thing lands
      // harder. Both fall out of the catalogue.
      radius: ft(broke ? fragile.noise : definition.noise ?? 6) * Math.min(1.6, 0.5 + energy / 40),
    });

    if (broke) {
      bus.emit(EVENT.BREAK, { kind: shot.kind, position: { ...at }, actor: shot.actor });
      for (let i = 0; i < (fragile.pieces ?? 2); i += 1) {
        const angle = (i / (fragile.pieces ?? 2)) * Math.PI * 2;
        entities.spawn({
          kind: fragile.debris,
          position: {
            x: at.x + Math.cos(angle) * 0.35,
            y: 0,
            z: at.z + Math.sin(angle) * 0.35,
          },
        });
      }
      return;
    }

    // Survived. It is furniture again, wherever it landed.
    entities.spawn({
      kind: shot.kind,
      variant: shot.variant,
      claim: shot.claim,
      position: { x: at.x, y: Math.max(0, at.y), z: at.z },
    });
  }

  function update(dt) {
    if (!flying.length) return;
    const next = [];

    for (const shot of flying) {
      shot.age += dt;
      const from = { ...shot.position };
      shot.velocity.y -= GRAVITY * dt;
      const to = {
        x: from.x + shot.velocity.x * dt,
        y: from.y + shot.velocity.y * dt,
        z: from.z + shot.velocity.z * dt,
      };

      const hit = bodyHit(from, to);
      if (hit) {
        shot.position = hit.at;
        resolve(shot, hit.at, { type: 'body', id: hit.body.id, kind: hit.body.kind ?? 'npc' });
        continue;
      }

      if (to.y <= 0) {
        // The floor. Interpolate to where it actually met it.
        const t = from.y > 0 ? from.y / (from.y - to.y) : 0;
        const at = { x: from.x + (to.x - from.x) * t, y: 0, z: from.z + (to.z - from.z) * t };
        shot.position = at;
        resolve(shot, at, { type: 'ground' });
        continue;
      }

      if (!segmentClear(from, to, world)) {
        shot.position = from;
        resolve(shot, from, { type: 'wall' });
        continue;
      }

      shot.position = to;
      const speed = Math.hypot(shot.velocity.x, shot.velocity.y, shot.velocity.z);
      if (shot.age > MAX_FLIGHT || speed < REST_SPEED) {
        resolve(shot, { ...to, y: Math.max(0, to.y) }, { type: 'ground' });
        continue;
      }
      next.push(shot);
    }

    flying = next;
  }

  return {
    launch,
    drop,
    update,
    get flying() {
      return flying;
    },
    clear() {
      flying = [];
    },
  };
}
